import config from "../../../../config";

/**
 * Claim-form upload + extraction.
 *
 * Lives in `shared/` because more than one surface reads it — the Insurance
 * Verification composer's submissions step, the FTC-side RequestedEstimatesPanel
 * (Estimates and Claims tabs) and the estimate upload modal's title options.
 * One copy, one place to fix.
 */

function authHeader() {
  const user = JSON.parse(localStorage.getItem("user"));
  return user && user.key ? { Authorization: "Token  " + user.key } : {};
}

/** Fields the office must supply per estimate when typing it in, in the order
 *  they read. With a claim form attached they are optional — see estimateGaps. */
export const ESTIMATE_FIELDS = [
  "amount",
  "service_date",
  "patient_name",
  "provider_name_raw",
  "cda_itrans_number",
];

export const ESTIMATE_FIELD_LABELS = {
  amount: "Amount",
  service_date: "Date of service",
  patient_name: "Patient name",
  provider_name_raw: "Provider",
  cda_itrans_number: "CDA / iTrans #",
};

/**
 * An empty estimate row.
 *
 * `cdaItrans` pre-fills the CDA/iTrans number from the office's insurance
 * profile. It identifies the office to the carrier and is the same on every claim
 * they send, so asking for it per estimate was asking for a constant to be
 * re-typed. Typing one here when the office has none saved sends it back to the
 * profile, so it is asked for once ever.
 */
export function blankEstimate(cdaItrans) {
  return {
    amount: "",
    service_date: null,
    patient_name: "",
    provider_name_raw: "",
    cda_itrans_number: cdaItrans || "",
    // [{ code, description, tooth }] — optional, so it never gates a request.
    procedures: [],
    claim_form_path: null,
    claim_form_name: null,
  };
}

/**
 * Procedure codes for the picker, scoped to the PATIENT's office.
 *
 * Not the shared getProcedure(): that one reads the office from
 * localStorage.selectedOffice, which is the header's office and not necessarily
 * the patient's — an FTC user works across offices and would search the wrong
 * catalogue, or an empty one.
 */
export async function searchProcedures(patientId, term) {
  if (!patientId) return [];
  try {
    const query = term ? `&q=${encodeURIComponent(term)}` : "";
    const response = await fetch(
      new Request(
        `${config.apiGateway.URL}/clinics/estimate-procedure-search/?patient_id=${encodeURIComponent(patientId)}${query}`,
        { method: "GET", headers: authHeader() },
      ),
    );
    if (!response.ok) return [];
    const body = await response.json();
    return (body && body.results) || [];
  } catch (e) {
    return [];
  }
}

/** The office-level values that pre-fill an estimate request, by patient. */
export async function fetchEstimateDefaults(patientId) {
  if (!patientId) return {};
  try {
    const response = await fetch(
      new Request(
        `${config.apiGateway.URL}/clinics/estimate-request-defaults/?patient_id=${encodeURIComponent(patientId)}`,
        { method: "GET", headers: authHeader() },
      ),
    );
    if (!response.ok) return {};
    return (await response.json()) || {};
  } catch (e) {
    // A pre-fill is a convenience; the field is still typeable without it.
    return {};
  }
}

/**
 * Remember the office's CDA/iTrans number.
 *
 * The estimate path already learns it from the first claim that carries one
 * (`_remember_office_cda_itrans` server-side). This is the same idea for the
 * BREAKDOWN path, which has no per-claim field to learn from and so had nowhere
 * to capture the number at all.
 *
 * Keyed by patient, like `fetchEstimateDefaults`, so the office is derived rather
 * than taken from the header — an FTC user works across offices.
 */
export async function saveEstimateDefaults(patientId, values) {
  if (!patientId) return { ok: false };
  try {
    const response = await fetch(
      new Request(`${config.apiGateway.URL}/clinics/estimate-request-defaults/`, {
        method: "PUT",
        headers: Object.assign({ "Content-Type": "application/json" }, authHeader()),
        body: JSON.stringify(Object.assign({ patient_id: patientId }, values || {})),
      }),
    );
    const json = await response.json().catch(function () { return {}; });
    return { ok: response.ok, json: json, status: response.status };
  } catch (e) {
    return { ok: false };
  }
}

/** Which of the typed fields are blank, regardless of whether a form covers them. */
export function missingEstimateFields(estimate) {
  const e = estimate || {};
  return ESTIMATE_FIELDS.filter(function (f) {
    const v = e[f];
    return v === null || v === undefined || String(v).trim() === "";
  });
}

/**
 * What the office still has to supply before this estimate can be sent.
 *
 * Nothing once a claim form is attached: the form carries the details, we read
 * what we can off it, and whatever the extractor could not read may stay blank
 * for the FTC user to take from the form itself. Typed by hand, every required
 * field. The server applies the same rule (REQUIRED_ESTIMATE_FIELDS).
 */
export function estimateGaps(estimate) {
  return estimate && estimate.claim_form_path ? [] : missingEstimateFields(estimate);
}

export function isEstimateComplete(estimate) {
  return estimateGaps(estimate).length === 0;
}

/**
 * Has the user put anything into this estimate yet?
 *
 * The difference between "not started" and "started but incomplete" matters: a
 * blank form is not a mistake, and styling it as one turns every new estimate
 * orange before the user has typed a character.
 */
export function isEstimateStarted(estimate) {
  if (estimate && estimate.claim_form_path) return true;
  return missingEstimateFields(estimate).length < ESTIMATE_FIELDS.length;
}

/**
 * "Amount and Provider", or "4 details" once naming them stops being useful.
 *
 * Listing five field names is a wall of text that reads as an error; a count
 * reads as progress. Names only earn their space when there are few enough that
 * they point at a next action.
 */
export function summariseMissingEstimateFields(fields) {
  const list = fields || [];
  if (!list.length) return "";
  if (list.length <= 2) return describeMissingEstimateFields(list);
  return list.length + " details";
}

/** "Amount, Provider and CDA / iTrans #" — the full list, when there is room. */
export function describeMissingEstimateFields(fields) {
  const names = (fields || []).map(function (f) {
    return ESTIMATE_FIELD_LABELS[f] || f;
  });
  if (!names.length) return "";
  if (names.length === 1) return names[0];
  return names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
}

/**
 * Upload a claim form and get its fields back.
 *
 * Deliberately slow — the server reads the form with a vision model so the office
 * can review what it found before submitting, which takes 5-20s. The caller shows
 * a progress state for that time.
 *
 * Never throws for an unreadable document: the server answers 200 with empty
 * fields and `extraction_empty: true`, because typing them in is a perfectly good
 * path and losing the upload on top of a bad read would not be.
 */
export async function extractClaimForm(patientId, file) {
  const body = new FormData();
  body.append("patient_id", patientId);
  body.append("file", file);

  const response = await fetch(
    new Request(`${config.apiGateway.URL}/clinics/estimate-claim-form/extract/`, {
      method: "POST",
      headers: authHeader(), // no Content-Type — the browser sets the multipart boundary
      body,
    }),
  );
  let json = null;
  try {
    json = await response.json();
  } catch (e) {
    json = null;
  }
  return { json, status: response.status };
}

/**
 * Read a claim form uploaded at the START of a request — before any patient is
 * chosen. The office comes from the caller (the wizard's selected office) and is
 * permission-checked server-side.
 *
 * Answers `{ claim_form_path, file_name, extraction_empty, document_kind,
 * patient, candidates, policies, claim }`: the patients in the office this may
 * be (each `match: "exact" | "close"`), the plans as policy previews
 * (`source: "claim_form"`), and the submission. Slow like extractClaimForm
 * (5-20s); never fails because the read did — an unreadable form still answers
 * 200 with `extraction_empty: true` and the file kept.
 */
export async function readClaimForm(officeId, file) {
  const body = new FormData();
  body.append("file", file);
  try {
    const response = await fetch(
      new Request(
        `${config.apiGateway.URL}/clinics/claim-form/read/?office_id=${encodeURIComponent(officeId)}`,
        { method: "POST", headers: authHeader(), body },
      ),
    );
    let json = null;
    try {
      json = await response.json();
    } catch (e) {
      json = null;
    }
    return { json, status: response.status };
  } catch (e) {
    return { json: null, status: 0 };
  }
}

async function postJson(path, payload) {
  try {
    const response = await fetch(
      new Request(`${config.apiGateway.URL}${path}`, {
        method: "POST",
        headers: Object.assign({ "Content-Type": "application/json" }, authHeader()),
        body: JSON.stringify(payload),
      }),
    );
    let json = null;
    try {
      json = await response.json();
    } catch (e) {
      json = null;
    }
    return { json, status: response.status };
  } catch (e) {
    return { json: null, status: 0 };
  }
}

/** The form's plans set against the patient the office chose: each comes back
 *  with `existing_local_policy_id` (+ field diffs) when the patient already has
 *  it. Reads only. */
export function matchClaimFormPolicies(patientId, previews) {
  return postJson("/clinics/claim-form/policies/", { patient_id: patientId, policies: previews || [] });
}

/**
 * Approve a plan read off the form: it becomes the patient's policy, under the
 * carrier the person settled on (a CarrierResolution choice — one of ours, or one
 * to add; a 409 is "we already have it", see parseCarrierConflict).
 */
export function saveClaimFormPolicy({ patientId, preview, carrierChoice, values }) {
  const carrier = (preview && preview.carrier) || {};
  return postJson("/clinics/claim-form/policies/save/", {
    patient_id: patientId,
    insurance_type: preview && preview.insurance_type,
    carrier_name_raw: carrier.name_raw || "",
    carrier_choice: carrierChoice,
    values: values || {},
  });
}

/**
 * The estimates outstanding on a policy — what the office asked us to chase.
 *
 * Extracted from RequestedEstimatesPanel, which had this fetch inline, because
 * the upload modal now needs the same rows to build its Request Title. Two
 * copies of the URL and the response shape is exactly how the two surfaces drift
 * apart.
 *
 * Returns `{ requestId, estimates, urgency, requests }` and swallows errors into
 * an empty list: both callers treat "no open request" and "couldn't load" the
 * same way — supporting context that simply isn't there — and neither should
 * interrupt the work the user came to do with an error banner.
 *
 * `scope` — "open" (the server default: the open request only, what the upload
 * modal wants for its title options) or "all", which also lists the fulfilled
 * history under `requests`, newest first: the office viewing what came back
 * sees what it submitted after the fact, not only while the ask is open. The
 * top-level keys always describe the open request only.
 */
export async function fetchPolicyRequestedEstimates(policyId, { requestType, scope } = {}) {
  const empty = { requestId: null, requestType: requestType || null, estimates: [], urgency: null, requests: [] };
  if (!policyId) return empty;
  try {
    // `requestType` picks WHICH submission-bearing request to read ("estimate"
    // — the server default — or "claim_followup"): the two can be open on one
    // policy at once, so the Estimates tab must never show a claim's rows and
    // vice versa. Every caller passes it explicitly.
    const query = `policy_id=${encodeURIComponent(policyId)}`
      + (requestType ? `&request_type=${encodeURIComponent(requestType)}` : "")
      + (scope ? `&scope=${encodeURIComponent(scope)}` : "");
    const response = await fetch(
      `${config.apiGateway.URL}/clinics/policy-requested-estimates/?${query}`,
      { method: "GET", headers: authHeader() },
    );
    if (!response.ok) return empty;
    const body = await response.json();
    return {
      requestId: (body && body.request_id) || null,
      requestType: (body && body.request_type) || requestType || null,
      estimates: (body && body.estimates) || [],
      urgency: (body && body.urgency) || null,
      requests: (body && body.requests) || [],
    };
  } catch (e) {
    return empty;
  }
}

/** Where a stored claim form can be viewed (access-checked server-side). */
export function claimFormUrl(estimateId) {
  return `${config.apiGateway.URL}/clinics/estimate-claim-form/${estimateId}/`;
}

/** "pdf" | "image" | "other" — what a browser can show inline, from the
 *  response type first and the file name when the server sent none. */
export function claimFormKind(contentType, fileName) {
  const type = (contentType || "").toLowerCase();
  const name = (fileName || "").toLowerCase();
  if (type === "application/pdf" || /\.pdf$/.test(name)) return "pdf";
  if (/^image\/(png|jpe?g|webp|gif)$/.test(type) || /\.(png|jpe?g|webp|gif)$/.test(name)) return "image";
  return "other";
}

/** Where a result's attachment (the carrier's response) can be viewed —
 *  access-checked server-side like the claim form. */
export function resultDocumentUrl(resultId) {
  return `${config.apiGateway.URL}/clinics/requested-estimate-results/${resultId}/document/`;
}

/**
 * Load a protected file (a claim form, a carrier's response) for an inline
 * preview.
 *
 * These files are PHI and come through access-checked views that need the auth
 * header, which an <iframe src> or <img src> cannot send — so the file is
 * fetched here and handed back as an object URL the viewer can embed. The
 * caller owns that URL and revokes it when done (URL.revokeObjectURL).
 *
 * Resolves to `{ url, kind }`; `url` is null when the file can't be loaded,
 * and `kind` is "other" for a type the browser can't render (TIFF, HEIC), in
 * which case the URL is still good for a download.
 */
export async function loadProtectedFileObjectUrl(url, fileName) {
  try {
    const response = await fetch(new Request(url, { method: "GET", headers: authHeader() }));
    if (!response.ok) return { url: null, kind: "other" };
    const blob = await response.blob();
    return { url: URL.createObjectURL(blob), kind: claimFormKind(blob.type, fileName) };
  } catch (e) {
    return { url: null, kind: "other" };
  }
}

/**
 * Results the FTC records against ONE requested submission.
 *
 * Always multipart, even without a file: one body shape for create and edit,
 * with `procedures` carried as a JSON string (the server parses it through the
 * same rules as the request's own procedures) and booleans as "true"/"false".
 * Blank strings clear a nullable field on the server, which is what an edit
 * that emptied an amount means.
 *
 * Every call resolves to `{ json, status }` like the rest of this module —
 * a 4xx is a value, not an exception, so describeApiError can read it.
 */
function resultFormData(values, file, opts) {
  const body = new FormData();
  Object.keys(values || {}).forEach(function (key) {
    const value = values[key];
    if (value === undefined) return;
    if (key === "procedures") {
      body.append("procedures", JSON.stringify(value || []));
    } else if (typeof value === "boolean") {
      body.append(key, value ? "true" : "false");
    } else {
      body.append(key, value === null ? "" : String(value));
    }
  });
  if (file) body.append("document", file);
  if (opts && opts.removeDocument) body.append("remove_document", "true");
  return body;
}

async function sendResult(url, method, body) {
  try {
    const response = await fetch(new Request(url, { method: method, headers: authHeader(), body: body }));
    let json = null;
    try {
      json = await response.json();
    } catch (e) {
      json = null;
    }
    return { json: json, status: response.status };
  } catch (e) {
    return { json: null, status: 0 };
  }
}

export function createSubmissionResult(estimateId, values, file) {
  return sendResult(
    `${config.apiGateway.URL}/clinics/requested-estimates/${estimateId}/results/`,
    "POST",
    resultFormData(values, file),
  );
}

export function updateSubmissionResult(resultId, values, file, opts) {
  return sendResult(
    `${config.apiGateway.URL}/clinics/requested-estimate-results/${resultId}/`,
    "PATCH",
    resultFormData(values, file, opts),
  );
}

export function deleteSubmissionResult(resultId) {
  return sendResult(`${config.apiGateway.URL}/clinics/requested-estimate-results/${resultId}/`, "DELETE");
}

/**
 * Editor state -> the API's `estimates` shape.
 *
 * Sends only COMPLETE estimates: an editor always shows at least one blank row,
 * and posting that would fail validation for a policy the user never intended to
 * add a second estimate to. Dates become plain YYYY-MM-DD because the server
 * parses strings, not dayjs objects.
 */
export function serializeEstimates(byPolicy, policyIds) {
  const out = {};
  (policyIds || []).forEach(function (policyId) {
    const rows = (byPolicy || {})[policyId] || [];
    const complete = rows.filter(isEstimateComplete).map(function (e) {
      return {
        amount: e.amount,
        service_date:
          e.service_date && e.service_date.format
            ? e.service_date.format("YYYY-MM-DD")
            : e.service_date,
        patient_name: e.patient_name,
        provider_name_raw: e.provider_name_raw,
        cda_itrans_number: e.cda_itrans_number,
        procedures: (e.procedures || []).filter(function (p) { return p && p.code; }),
        claim_form_path: e.claim_form_path || null,
      };
    });
    if (complete.length) out[String(policyId)] = complete;
  });
  return out;
}
